import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { UserEntity } from '@backstage/catalog-model';
import { InfoCard, Link } from '@backstage/core-components';
import { countryCodeToFlag } from '../../utils/countryFlag';
import { isSlotFilled, ROLES, supportsLeadership } from '../shared/leadershipSlots';

const useStyles = makeStyles(theme => ({
  roleLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  slot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: theme.spacing(1.5),
    gap: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  unassigned: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    fontSize: '0.875rem',
  },
}));

interface ResolvedUser {
  name: string;
  email: string;
  entityName?: string;
  picture?: string;
  country?: string;
}

export function EntityTeamLeadershipCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const [resolvedByKey, setResolvedByKey] = useState<Map<string, ResolvedUser>>(new Map());
  const [loading, setLoading] = useState(true);

  const annotations = entity.metadata.annotations ?? {};

  useEffect(() => {
    const tasks = ROLES.map(async ({ newKey, oldKey }) => {
      const ref = annotations[newKey];
      const email = annotations[oldKey];

      if (ref) {
        // New format: entity ref like "user:default/joel-harley"
        const e = await catalogApi.getEntityByRef(ref).catch(() => null);
        if (e) {
          const u = e as UserEntity;
          return [newKey, {
            name: u.spec?.profile?.displayName ?? u.metadata.name,
            email: u.spec?.profile?.email ?? '',
            entityName: u.metadata.name,
            picture: u.spec?.profile?.picture,
            country: u.metadata.annotations?.['user/country'],
          } satisfies ResolvedUser] as const;
        }
      }

      if (email) {
        // Old format: email address — resolve by scanning users
        const res = await catalogApi
          .getEntities({ filter: { kind: 'User' }, fields: ['metadata.name', 'metadata.annotations', 'spec.profile'] })
          .catch(() => ({ items: [] }));
        for (const item of res.items) {
          const u = item as UserEntity;
          if (u.spec?.profile?.email === email) {
            return [newKey, {
              name: u.spec?.profile?.displayName ?? u.metadata.name,
              email,
              entityName: u.metadata.name,
              picture: u.spec?.profile?.picture,
              country: u.metadata.annotations?.['user/country'],
            } satisfies ResolvedUser] as const;
          }
        }
        // email present but no matching User entity
        return [newKey, { name: email, email }] as const;
      }

      return null;
    });

    Promise.all(tasks)
      .then(results => {
        const map = new Map<string, ResolvedUser>();
        for (const r of results) {
          if (r) map.set(r[0], r[1]);
        }
        setResolvedByKey(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [catalogApi, entity]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!supportsLeadership(entity)) return null;

  return (
    <InfoCard title="Leadership" variant="gridItem">
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {ROLES.map(role => {
            const { newKey, label } = role;
            const hasValue = isSlotFilled(annotations, role);
            const user = resolvedByKey.get(newKey);
            const flag = user?.country ? countryCodeToFlag(user.country) : '';

            return (
              <Grid item xs={12} sm={6} key={newKey}>
                <Box className={classes.slot}>
                  <Typography className={classes.roleLabel}>{label}</Typography>
                  {user ? (
                    <>
                      <Avatar src={user.picture} style={{ width: 48, height: 48 }}>
                        {user.name.charAt(0)}
                      </Avatar>
                      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
                        {user.entityName ? (
                          <Link to={`/catalog/default/user/${user.entityName}`}>
                            {user.name}
                          </Link>
                        ) : (
                          <Typography>{user.name}</Typography>
                        )}
                        {flag && (
                          <span role="img" aria-label={`${user.country!.trim().toUpperCase()} flag`}>
                            {flag}
                          </span>
                        )}
                      </Box>
                      {user.email && (
                        <Typography variant="caption" color="textSecondary">
                          {user.email}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography className={classes.unassigned}>
                      {hasValue ? 'Unresolved' : 'Unassigned'}
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </InfoCard>
  );
}
